import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { REPORT_TEMPLATE_STATUS, pickBilingual, type Locale } from '@lims/shared-types';
import { catalogueApi, reportTemplatesApi, type CatalogueItem } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { ActionBar, ActionButton, Panel, SelectField, useToast } from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';

const DEFAULT = 'laboratory-default';

/**
 * WHICH REPORT TEMPLATE THIS TEST'S REPORTS USE (decision D254). Presentation
 * only: the laboratory default unless this test needs its own layout. Unlike
 * the specialised result template beside it, it is not gated on readiness.
 * Only an ACTIVE template of this laboratory can be chosen — the server and
 * the database refuse anything else.
 */
export function ReportTemplatePanel({ item }: { readonly item: CatalogueItem }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale() as Locale;
  const [choice, setChoice] = useState<string>(item.reportTemplateId ?? DEFAULT);

  useEffect(() => {
    setChoice(item.reportTemplateId ?? DEFAULT);
  }, [item.reportTemplateId]);

  const mayRead =
    can(PERMISSIONS.TEMPLATES_VIEW) || can(PERMISSIONS.REPORTS_PREPARE) || can(PERMISSIONS.REPORTS_ISSUE);
  const templates = useQuery({
    queryKey: ['report-templates', 'active-for-catalogue'],
    queryFn: () => reportTemplatesApi.list({ status: REPORT_TEMPLATE_STATUS.ACTIVE, pageSize: 100 }),
    enabled: mayRead,
  });

  const save = useMutation({
    mutationFn: () => catalogueApi.update(item.id, { reportTemplateId: choice === DEFAULT ? null : choice }),
    onSuccess: async (next) => {
      queryClient.setQueryData(['catalogue', item.id], next);
      await queryClient.invalidateQueries({ queryKey: ['catalogue'] });
      await queryClient.invalidateQueries({ queryKey: ['report-templates'] });
      // From the server's confirmation, never from the button press.
      show({ tone: 'success', message: t('catalogue.reportTemplate.saved') });
    },
  });

  if (!mayRead) return null;
  const unchanged = choice === (item.reportTemplateId ?? DEFAULT);

  return (
    <Panel title={t('catalogue.reportTemplate.title')} description={t('catalogue.reportTemplate.description')}>
      <div className="flex flex-col gap-stack" data-testid="catalogue-report-template">
        <div className="max-w-md">
          <SelectField
            label={t('catalogue.reportTemplate.label')}
            value={choice}
            isLoading={templates.isLoading}
            onChange={(value) => setChoice(value ?? DEFAULT)}
            options={[
              { value: DEFAULT, label: t('catalogue.reportTemplate.default') },
              ...(templates.data?.data ?? []).map((template) => ({
                value: template.id,
                label: pickBilingual(template.name, locale) ?? t('common.notRecorded'),
              })),
            ]}
          />
        </div>
        {save.error ? <ErrorNotice error={save.error} /> : null}
        <ActionBar>
          <ActionButton priority="secondary" isDisabled={unchanged} isLoading={save.isPending} onPress={() => save.mutate()}>
            {t('catalogue.reportTemplate.save')}
          </ActionButton>
        </ActionBar>
      </div>
    </Panel>
  );
}
